'use client'

import { useAuthorization } from '@entities/authentication'
import { getReservedLessons } from '@entities/lesson'
import { userEntityMutations, userEntityQueries } from '@entities/users'
import { PaymentTermsInformationBottomSheet } from '@features/payment'
import {
  requestOnboardingBookingConfirm,
  requestOnboardingBookingOptions,
  requestOnboardingMarketingConsent,
  requestOnboardingState,
} from '@features/trial-booking/api'
import { EN_ONBOARDING_LEVEL_LABELS, KO_ONBOARDING_LEVEL_LABELS } from '@features/trial-booking/level-labels'
import { getTrialLevelLabelFromClassroomParams, type OnboardingBookingSlot } from '@features/trial-booking/model'
import { track, trackEvent } from '@shared/analytics'
import { useI18n, type TranslationValues } from '@shared/config/i18n'
import { useNativeBridgeStore } from '@shared/models'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { HomeBannerCarousel } from '@widgets/home-banners'
import clsx from 'clsx'
import { Check, X } from 'lucide-react'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { BottomSheetRoot, BottomSheetTrigger } from '@podo-app/design-system-temp'
import { useFlag } from '@podo/flags/react'

import {
  confirmJpTrialEmailVerificationCode,
  invalidateJpTrialEmailVerificationCode,
  requestJpTrialEmailVerificationCode,
  requestJpTrialEmailVerificationStatus,
  type JpTrialEmailVerification,
} from './api'
import { JpAppDownloadPanel } from './app-download-panel'
import {
  applyJpTrialSlotPolicy,
  applyJpTrialRangePolicy,
  classifyJpTrialBookingError,
  createBookingIntentKey,
  getJpTrialSlotEmptyReason,
  getNextJpTrialRangeDays,
  hasCompletedJpTrialProfile,
  isValidEnglishNickname,
  JP_TRIAL_SLOT_RANGE_FEATURE_KEY,
  normalizeJpTrialInitialRangeDays,
  sanitizeEnglishNickname,
  type JpTrialBookingOptions,
  type JpTrialLessonLanguage,
} from './model'
import { dismissJpTrialApplicationForSession } from './session-dismissal'

type LessonLanguage = JpTrialLessonLanguage

type Profile = {
  name: string | null
  email: string | null
  phone: string | null
  gender: 'F' | 'M' | null
  timezoneCode: string | null
}

type BookingOptions = JpTrialBookingOptions

// PRD/GT_CLASS_COURSE.BOOK_TYPE 난이도 순서: BEGINNER(A) → INTERMEDIATE(B) → PRE_ADVANCED(C) → ADVANCED(D)
const TRIAL_LEVELS = [
  { value: 'BEGINNER', bars: 1, featured: true },
  { value: 'INTERMEDIATE', bars: 2, featured: false },
  { value: 'PRE_ADVANCED', bars: 3, featured: false },
  { value: 'ADVANCED', bars: 4, featured: false },
] as const

const parseUtcBookingTime = (value: string | null | undefined) => {
  if (!value) {
    return NaN
  }
  // 상태 조회 API의 LocalDateTime은 UTC 시각이지만 타임존 표기가 없다.
  return Date.parse(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value}Z`)
}

const TRIAL_DURATION_MINUTES: Record<LessonLanguage, 25 | 55> = {
  ko: 55,
  en: 25,
}

const JP_TRIAL_TERMS_VERSION = 'jp-trial-terms-2026-07-27-v1'
const JP_TRIAL_DRAFT_STORAGE_KEY = 'podo:jp-trial-application:draft:v1'
const OPTIONS_REFRESH_INTERVAL_MS = 60_000
const CUSTOMER_CENTER_URL = '/my-podo/customer-center/inquiries/new'

/**
 * GA 로 나가는 JP 체험 신청 이벤트명. ClickHouse 에는 `jp_` 를 붙여 보낸다 —
 * KR 체험 퍼널(`trial_payment_*` 등)과 한 테이블에 쌓이므로 시장을 이름으로 갈라야 한다
 * (기존 `jp_payment_*` 와 같은 규칙).
 */
type JpTrialEventName =
  | 'trial_apply_view'
  | 'trial_language_select'
  | 'trial_level_select'
  | 'trial_slot_select'
  | 'trial_slot_empty_view'
  | 'trial_slot_refresh'
  | 'trial_range_expanded'
  | 'trial_bottomsheet_open'
  | 'trial_bottomsheet_confirm'
  | 'trial_agree_toggle'
  | 'trial_cta_click'
  | 'trial_booking_result'
  | 'trial_complete_view'
  | 'trial_post_booking_route'
  | 'trial_race_dialog_view'
  | 'trial_race_dialog_action'
  | 'trial_duplicate_block'
  | 'trial_duplicate_email_block'
  | 'trial_email_verification_request'
  | 'trial_email_verification_confirm'
  | 'trial_session_expired'
  | 'trial_cs_contact_click'

const trackJpTrialEvent = (
  eventName: JpTrialEventName,
  lessonLanguage: LessonLanguage,
  properties: Record<string, unknown> = {},
) => {
  const payload = { ...properties, lesson_language: lessonLanguage }
  try {
    trackEvent(eventName, payload)
  } catch {
    // 분석 도구 오류는 신청 흐름에 영향을 주지 않는다.
  }
  try {
    track(`jp_${eventName}`, payload)
  } catch {
    // 분석 도구 오류는 신청 흐름에 영향을 주지 않는다.
  }
}

const ERROR_MESSAGE_KEY = {
  SLOT_UNAVAILABLE: 'lesson.jpTrial.errors.slotUnavailable',
  TOO_SOON: 'lesson.jpTrial.errors.slotUnavailable',
  ALREADY_BOOKED: 'lesson.jpTrial.errors.alreadyBooked',
  EMAIL_VERIFICATION_REQUIRED: 'lesson.jpTrial.errors.emailVerificationRequired',
  RETRYABLE: 'lesson.jpTrial.errors.retryable',
  FATAL: 'lesson.jpTrial.errors.fatal',
} as const

type TrialApplicationDraft = {
  lowerLanguage?: LessonLanguage
  level?: (typeof TRIAL_LEVELS)[number]['value']
  selectedSlot?: OnboardingBookingSlot | null
  requiredAgreed?: boolean
  marketingAgreed?: boolean
  intentKey?: string
}

const normalizeEmail = (value: string) => value.trim().toLowerCase()
const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeEmail(value))

const EMAIL_STATUS_MESSAGE_KEY: Partial<Record<JpTrialEmailVerification['status'], string>> = {
  CODE_SENT: 'lesson.jpTrial.email.status.codeSent',
  COOLDOWN: 'lesson.jpTrial.email.status.cooldown',
  INVALID_CODE: 'lesson.jpTrial.email.status.invalidCode',
  ATTEMPTS_EXCEEDED: 'lesson.jpTrial.email.status.attemptsExceeded',
  CODE_EXPIRED: 'lesson.jpTrial.email.status.codeExpired',
  LOCKED: 'lesson.jpTrial.email.status.locked',
  RATE_LIMITED: 'lesson.jpTrial.email.status.rateLimited',
  SEND_FAILED: 'lesson.jpTrial.email.status.sendFailed',
  VERIFIED: 'lesson.jpTrial.email.status.verified',
}

type LocalizedMessage = {
  key: string
  values?: TranslationValues
}

const message = (key: string, values?: TranslationValues): LocalizedMessage => ({ key, values })

const readDraft = (): TrialApplicationDraft => {
  if (typeof window === 'undefined') {
    return {}
  }
  try {
    return JSON.parse(window.sessionStorage.getItem(JP_TRIAL_DRAFT_STORAGE_KEY) ?? '{}') as TrialApplicationDraft
  } catch {
    return {}
  }
}

const isOpenSlot = (options: BookingOptions, utcClassDateTime: string) =>
  Object.values(options.timeGroupsByDate)
    .flat()
    .flatMap((group) => group.times)
    .some((slot) => slot.utcClassDateTime === utcClassDateTime && slot.status === 'open')

const CheckCircle = ({ selected, disabled = false }: { selected: boolean; disabled?: boolean }) => (
  <span
    className={clsx(
      'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-white',
      selected ? 'bg-[#6ABE36]' : disabled ? 'bg-[#D6D6D6]' : 'bg-[#D9D9D9]',
    )}
  >
    <Check size={13} strokeWidth={3} />
  </span>
)

const LevelIcon = ({ bars }: { bars: number }) => (
  <span className="flex h-[46px] w-[46px] shrink-0 items-end justify-center gap-[4px] rounded-full bg-white pb-[11px]">
    {[1, 2, 3].map((bar) => (
      <span
        key={bar}
        className={clsx('w-1 rounded-full', bar <= bars ? 'bg-[#6ABE36]' : 'border border-[#6ABE36]')}
        style={{ height: `${5 + bar * 5}px` }}
      />
    ))}
  </span>
)

const SelectionCard = ({
  children,
  compact = false,
  disabled,
  onClick,
  selected,
}: {
  children: React.ReactNode
  compact?: boolean
  disabled?: boolean
  onClick?: () => void
  selected: boolean
}) => {
  const cardClassName = clsx(
    'flex w-full items-center gap-3 rounded-[12px] border px-[18px] text-left transition-colors',
    compact ? 'min-h-[64px] py-3' : 'min-h-[86px] py-4',
    selected ? 'border-[1.5px] border-[#6ABE36] bg-[#F2FCEC]' : 'border-[#D6D6D6] bg-white',
    disabled && 'cursor-not-allowed opacity-55',
  )
  const content = (
    <>
      {children}
      <span className="ml-auto">
        <CheckCircle selected={selected} disabled={disabled} />
      </span>
    </>
  )

  if (!onClick) {
    return <div className={cardClassName}>{content}</div>
  }

  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={selected}
      onClick={onClick}
      className={clsx(
        cardClassName,
        'outline-none focus-visible:ring-2 focus-visible:ring-[#6ABE36] focus-visible:ring-offset-1',
      )}
    >
      {content}
    </button>
  )
}

const CalendarSheet = ({
  dates,
  onClose,
  onCommit,
  selectedSlot,
  timeGroupsByDate,
}: BookingOptions & {
  onClose: () => void
  onCommit: (slot: OnboardingBookingSlot) => void
  selectedSlot: OnboardingBookingSlot | null
}) => {
  const { t } = useI18n()
  const [dateIndex, setDateIndex] = useState(0)
  const [pendingSlot, setPendingSlot] = useState<OnboardingBookingSlot | null>(selectedSlot)
  const dragStartYRef = useRef<number | null>(null)
  const date = dates[dateIndex]
  const timeGroups = date ? (timeGroupsByDate[date.fullDate] ?? []) : []

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end bg-[rgba(0,0,0,0.45)]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="jp-trial-calendar-title"
    >
      <button
        type="button"
        aria-label={t('lesson.jpTrial.schedule.close')}
        className="absolute inset-0"
        onClick={onClose}
      />
      <div className="relative max-h-[88dvh] w-full overflow-y-auto rounded-t-[24px] bg-white px-5 pb-[calc(20px+env(safe-area-inset-bottom))] pt-5">
        <div
          className="-mt-3 mb-2 flex h-6 touch-none items-center justify-center"
          onTouchStart={(event) => {
            dragStartYRef.current = event.touches[0]?.clientY ?? null
          }}
          onTouchEnd={(event) => {
            const startY = dragStartYRef.current
            const endY = event.changedTouches[0]?.clientY
            dragStartYRef.current = null
            if (startY !== null && endY !== undefined && endY - startY > 64) {
              onClose()
            }
          }}
        >
          <span className="h-1 w-10 rounded-full bg-[#D6DADC]" aria-hidden="true" />
        </div>
        <div className="mb-4">
          <h2 id="jp-trial-calendar-title" className="text-[20px] font-bold">
            {t('lesson.jpTrial.schedule.title')}
          </h2>
        </div>
        <div className="mb-10 grid grid-cols-7 gap-1">
          {dates.map((item, index) => (
            <button
              key={item.fullDate}
              type="button"
              onClick={() => setDateIndex(index)}
              style={{
                borderColor: index === dateIndex ? '#6ABE36' : 'transparent',
              }}
              className={clsx(
                'min-w-0 rounded-[10px] border px-1 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[#6ABE36] focus-visible:ring-offset-1',
                index === dateIndex ? 'border-[1.5px] bg-[#F2FCEC]' : 'border-transparent',
              )}
            >
              <span className="block text-xs">{item.day}</span>
              <span className="font-semibold">{item.date}</span>
            </button>
          ))}
        </div>
        <div className="mb-5 flex items-center justify-between">
          <h3 className="font-bold">{t('lesson.jpTrial.schedule.available')}</h3>
          <span className="flex items-center gap-2 text-sm text-[#A0A0A0]">
            <span className="h-3 w-3 rounded-full bg-[#D6D6D6]" />
            {t('lesson.jpTrial.schedule.full')}
          </span>
        </div>
        <div className="space-y-5">
          {timeGroups.map((group) => (
            <section key={group.groupName}>
              <h4 className="mb-2 text-sm text-[#A0A0A0]">{group.groupName}</h4>
              <div className="grid grid-cols-3 gap-2">
                {group.times.map((slot) => {
                  const candidate = {
                    id: `${date?.fullDate}-${slot.time}`,
                    label: `${date?.day ?? ''} ${slot.time}`,
                    subtitle: date?.fullDate ?? '',
                    fullDate: date?.fullDate ?? '',
                    time: slot.time,
                    utcClassDateTime: slot.utcClassDateTime,
                  }
                  const selected = pendingSlot?.utcClassDateTime === slot.utcClassDateTime
                  const open = slot.status === 'open' && Boolean(slot.utcClassDateTime)

                  return (
                    <button
                      key={`${slot.time}-${slot.utcClassDateTime}`}
                      type="button"
                      disabled={!open}
                      aria-pressed={selected}
                      aria-label={open ? slot.time : t('lesson.jpTrial.schedule.closedTime', { time: slot.time })}
                      onClick={() => setPendingSlot(candidate)}
                      className={clsx(
                        'min-h-[52px] rounded-[12px] border px-1 text-base outline-none focus-visible:ring-2 focus-visible:ring-[#6ABE36] focus-visible:ring-offset-1',
                        selected ? 'border-[1.5px] border-[#6ABE36] bg-[#F2FCEC] font-semibold' : 'border-[#E8E8E8]',
                        !open && 'bg-[#F5F5F5] text-[#D6D6D6]',
                      )}
                    >
                      {slot.time}
                    </button>
                  )
                })}
              </div>
            </section>
          ))}
        </div>
        <button
          type="button"
          disabled={!pendingSlot?.utcClassDateTime}
          onClick={() => {
            if (pendingSlot?.utcClassDateTime) {
              onCommit(pendingSlot)
            }
          }}
          className="mt-8 h-[52px] w-full rounded-[10px] border border-[#111] bg-[#B5FD4C] font-bold text-[#111] shadow-[0_5px_0_#111] outline-none focus-visible:ring-2 focus-visible:ring-[#6ABE36] focus-visible:ring-offset-1 disabled:border-[#E8E8E8] disabled:bg-[#F5F5F5] disabled:text-[#D6D6D6] disabled:shadow-[0_5px_0_#E8E8E8]"
        >
          {t('lesson.jpTrial.schedule.selectTime')}
        </button>
      </div>
    </div>
  )
}

const RaceDialog = ({ onConfirm }: { onConfirm: () => void }) => {
  const { t } = useI18n()

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-[rgba(0,0,0,0.5)] px-5"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="jp-trial-race-title"
    >
      <div className="w-full max-w-[360px] rounded-[20px] bg-white p-6 text-center">
        <h2 id="jp-trial-race-title" className="text-xl font-bold">
          {t('lesson.jpTrial.schedule.raceTitle')}
        </h2>
        <p className="mt-3 text-sm leading-6 text-[#757A86]">{t('lesson.jpTrial.schedule.raceDescription')}</p>
        <button
          type="button"
          onClick={onConfirm}
          className="mt-6 h-[52px] w-full rounded-[10px] border border-[#111] bg-[#B5FD4C] font-bold text-[#111] shadow-[0_5px_0_#111]"
        >
          {t('lesson.jpTrial.schedule.raceAction')}
        </button>
      </div>
    </div>
  )
}

const DuplicateEmailDialog = ({ onClose, onContact }: { onClose: () => void; onContact: () => void }) => {
  const { t } = useI18n()

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center bg-[rgba(0,0,0,0.5)] px-5"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="jp-trial-duplicate-email-title"
    >
      <div className="w-full max-w-[360px] rounded-[20px] bg-white p-6 text-center">
        <h2 id="jp-trial-duplicate-email-title" className="text-xl font-bold">
          {t('lesson.jpTrial.email.duplicate.title')}
        </h2>
        <p className="mt-3 text-sm leading-6 text-[#757A86]">{t('lesson.jpTrial.email.duplicate.description')}</p>
        <button
          type="button"
          onClick={onContact}
          className="mt-6 h-[52px] w-full rounded-[10px] border border-[#111] bg-[#B5FD4C] font-bold text-[#111] shadow-[0_5px_0_#111]"
        >
          {t('lesson.jpTrial.email.duplicate.contact')}
        </button>
        <button type="button" onClick={onClose} className="mt-3 h-10 w-full text-sm font-bold text-[#676C76]">
          {t('lesson.jpTrial.email.duplicate.useAnother')}
        </button>
      </div>
    </div>
  )
}

const CompletionDialog = ({
  bookedSlotLabel,
  durationMinutes,
  showLearningConsultation,
  isApp,
  onHome,
  onAppOpen,
  onAppDownload,
}: {
  bookedSlotLabel: string
  durationMinutes: 25 | 55
  showLearningConsultation: boolean
  isApp: boolean
  onHome: () => void
  onAppOpen: () => void
  onAppDownload: () => void
}) => {
  const { t } = useI18n()

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-[rgba(0,0,0,0.5)] px-5"
      role="dialog"
      aria-modal="true"
      aria-label={t('lesson.jpTrial.completion.dialogLabel')}
    >
      <div className="w-full max-w-[335px]">
        <div className={clsx('rounded-[20px] bg-white p-5 text-center', !isApp && 'md:hidden')}>
          <h2 className="text-xl font-bold">{t('lesson.jpTrial.completion.title')}</h2>
          <div className="mt-3 rounded-[12px] border-[1.5px] border-[#6ABE36] bg-[#F2FCEC] px-[18px] py-4 text-left">
            <p className="text-sm font-medium text-[#6ABE36]">{t('lesson.jpTrial.completion.lesson')}</p>
            {showLearningConsultation ? (
              <>
                <p className="mt-1 text-2xl font-bold">{bookedSlotLabel}</p>
                <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-[#6ABE36]">
                  <span className="rounded-full bg-[#6ABE36] px-2 py-1 font-bold text-white">
                    {t('lesson.jpTrial.completion.freeTrial', { minutes: durationMinutes })}
                  </span>
                  <span>{t('lesson.jpTrial.ticket.consultation')}</span>
                </div>
              </>
            ) : (
              <div className="mt-1 flex items-center gap-2">
                <p className="text-2xl font-bold">{bookedSlotLabel}</p>
                <span className="shrink-0 rounded-full bg-[#6ABE36] px-2 py-1 text-[10px] font-bold text-white">
                  {t('lesson.jpTrial.completion.freeTrial', { minutes: durationMinutes })}
                </span>
              </div>
            )}
          </div>
          <p className="mt-3 whitespace-nowrap text-xs text-[#777]">{t('lesson.jpTrial.completion.emailSent')}</p>
          <button
            type="button"
            onClick={isApp ? onHome : onAppOpen}
            className="mt-6 h-[52px] w-full rounded-[10px] border border-[#111] bg-[#B5FD4C] font-bold text-[#111] shadow-[0_5px_0_#111]"
          >
            {t(isApp ? 'lesson.jpTrial.completion.home' : 'lesson.jpTrial.completion.openApp')}
          </button>
        </div>
        {!isApp ? (
          <div className="hidden md:block">
            <JpAppDownloadPanel onNavigate={onAppDownload} />
          </div>
        ) : null}
      </div>
    </div>
  )
}

type JpTrialApplicationViewProps = {
  availableLanguages: LessonLanguage[]
  experimentTracker?: ReactNode
  initialLanguage?: LessonLanguage
  initialMarketingAgreed: boolean
  profile: Profile
}

export const JpTrialApplicationView = ({
  availableLanguages,
  experimentTracker,
  initialLanguage: requestedInitialLanguage,
  initialMarketingAgreed,
  profile,
}: JpTrialApplicationViewProps) => {
  const router = useRouter()
  const queryClient = useQueryClient()
  const { effectiveLocale, t } = useI18n()
  const { bearerToken, uid: userId } = useAuthorization()
  const slotRangeFlag = useFlag(JP_TRIAL_SLOT_RANGE_FEATURE_KEY)
  const { from } = useNativeBridgeStore()
  const isApp = from === 'APP'
  const initialDraftRef = useRef<TrialApplicationDraft | null>(null)
  if (initialDraftRef.current === null) {
    initialDraftRef.current = readDraft()
  }
  const initialLanguage: LessonLanguage =
    requestedInitialLanguage && availableLanguages.includes(requestedInitialLanguage)
      ? requestedInitialLanguage
      : availableLanguages.includes('ko')
        ? 'ko'
        : 'en'
  const initialDraft =
    initialDraftRef.current.lowerLanguage === initialLanguage
      ? initialDraftRef.current
      : ({} satisfies TrialApplicationDraft)
  const [language, setLanguage] = useState<LessonLanguage>(initialLanguage)
  const [nickname, setNickname] = useState(() => sanitizeEnglishNickname(profile.name ?? ''))
  const [nicknameTouched, setNicknameTouched] = useState(false)
  const [email, setEmail] = useState(() => profile.email ?? '')
  const [emailTouched, setEmailTouched] = useState(false)
  const [emailVerification, setEmailVerification] = useState<JpTrialEmailVerification | null>(null)
  const [emailVerificationCode, setEmailVerificationCode] = useState('')
  const [emailVerificationBusy, setEmailVerificationBusy] = useState(false)
  const [emailStatusMessage, setEmailStatusMessage] = useState<LocalizedMessage | null>(null)
  const [duplicateEmailDialogOpen, setDuplicateEmailDialogOpen] = useState(false)
  const [clockNow, setClockNow] = useState(Date.now)
  const [emailRetryAvailableAt, setEmailRetryAvailableAt] = useState<number | null>(null)
  const [serverClockOffsetMs, setServerClockOffsetMs] = useState(0)
  const [level, setLevel] = useState<(typeof TRIAL_LEVELS)[number]['value']>(() =>
    initialDraft.level && TRIAL_LEVELS.some((item) => item.value === initialDraft.level)
      ? initialDraft.level
      : 'BEGINNER',
  )
  const [options, setOptions] = useState<BookingOptions | null>(null)
  const initialRangeDays = normalizeJpTrialInitialRangeDays(slotRangeFlag.value, Boolean(userId))
  const [visibleRangeDays, setVisibleRangeDays] = useState(initialRangeDays)
  const [selectedSlot, setSelectedSlot] = useState<OnboardingBookingSlot | null>(initialDraft.selectedSlot ?? null)
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [requiredAgreed, setRequiredAgreed] = useState(initialDraft.requiredAgreed ?? false)
  const [marketingAgreed, setMarketingAgreed] = useState(
    initialMarketingAgreed || (initialDraft.marketingAgreed ?? false),
  )
  const [intentKey, setIntentKey] = useState(initialDraft.intentKey || createBookingIntentKey)
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const submittingRef = useRef(false)
  const [errorMessage, setErrorMessage] = useState<LocalizedMessage | null>(null)
  const [availabilityNotice, setAvailabilityNotice] = useState<LocalizedMessage | null>(null)
  const [raceDialogOpen, setRaceDialogOpen] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [completedSlotLabel, setCompletedSlotLabel] = useState('')
  const optionsRef = useRef<BookingOptions | null>(null)
  const fullOptionsRef = useRef<BookingOptions | null>(null)
  const visibleRangeDaysRef = useRef(initialRangeDays)
  const selectedSlotRef = useRef<OnboardingBookingSlot | null>(selectedSlot)
  const requestSequenceRef = useRef(0)
  const sessionRedirectingRef = useRef(false)
  const previousLevelRef = useRef(level)
  const emptyTrackedKeysRef = useRef(new Set<string>())
  const activeVerificationEmailRef = useRef<string | null>(null)
  const updateProfile = useMutation(userEntityMutations.updateProfile({ bearerToken }))

  const normalizedEmail = normalizeEmail(email)
  const emailValid = isValidEmail(email)
  const emailVerified =
    Boolean(emailVerification?.verified) &&
    Boolean(emailVerification?.email) &&
    normalizeEmail(emailVerification?.email ?? '') === normalizedEmail
  const serverNow = clockNow + serverClockOffsetMs
  const resendSeconds = emailVerification?.resendAvailableAt
    ? Math.max(0, Math.ceil((emailVerification.resendAvailableAt - serverNow) / 1000))
    : 0
  const retrySeconds = emailRetryAvailableAt ? Math.max(0, Math.ceil((emailRetryAvailableAt - clockNow) / 1000)) : 0
  const showVerificationCode =
    !emailVerified &&
    ['CODE_SENT', 'COOLDOWN', 'INVALID_CODE'].includes(emailVerification?.status ?? '') &&
    Boolean(activeVerificationEmailRef.current)
  const displayedRecommendedSlots = useMemo(() => {
    if (!options || !selectedSlot?.utcClassDateTime) {
      return options?.recommendedSlots.slice(0, 6) ?? []
    }
    if (options.recommendedSlots.some((slot) => slot.utcClassDateTime === selectedSlot.utcClassDateTime)) {
      return options.recommendedSlots.slice(0, 6)
    }

    return [...options.recommendedSlots.slice(0, 5), selectedSlot].sort((a, b) =>
      (a.utcClassDateTime ?? '').localeCompare(b.utcClassDateTime ?? ''),
    )
  }, [options, selectedSlot])

  useEffect(() => {
    selectedSlotRef.current = selectedSlot
  }, [selectedSlot])

  useEffect(() => {
    if (!emailVerification?.resendAvailableAt && !emailRetryAvailableAt) {
      return
    }

    const timer = window.setInterval(() => setClockNow(Date.now()), 1_000)
    return () => window.clearInterval(timer)
  }, [emailRetryAvailableAt, emailVerification?.resendAvailableAt])

  useEffect(() => {
    if (!bearerToken) {
      return
    }

    let active = true
    setEmailVerificationBusy(true)
    void requestJpTrialEmailVerificationStatus(bearerToken)
      .then((result) => {
        if (!active) {
          return
        }
        setEmailVerification(result)
        if (result.serverTime) {
          setServerClockOffsetMs(result.serverTime - Date.now())
        }
        if (result.email) {
          setEmail((current) => current || result.email || '')
        }
        if (['CODE_SENT', 'COOLDOWN', 'INVALID_CODE'].includes(result.status) && result.email) {
          activeVerificationEmailRef.current = normalizeEmail(result.email)
        }
        const statusMessageKey = EMAIL_STATUS_MESSAGE_KEY[result.status]
        setEmailStatusMessage(statusMessageKey ? message(statusMessageKey) : null)
      })
      .catch(() => {
        if (active) {
          setEmailStatusMessage(message('lesson.jpTrial.email.status.loadFailed'))
        }
      })
      .finally(() => {
        if (active) {
          setEmailVerificationBusy(false)
        }
      })

    return () => {
      active = false
    }
  }, [bearerToken])

  useEffect(() => {
    trackJpTrialEvent('trial_apply_view', initialLanguage, {
      country: 'JP',
      market: 'JP',
      language: 'ja',
    })
  }, [initialLanguage])

  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        JP_TRIAL_DRAFT_STORAGE_KEY,
        JSON.stringify({
          lowerLanguage: language,
          level,
          selectedSlot,
          requiredAgreed,
          marketingAgreed,
          intentKey,
        } satisfies TrialApplicationDraft),
      )
    } catch {
      // 세션 스토리지 제한은 신청 동작 자체를 막지 않는다.
    }
  }, [intentKey, language, level, marketingAgreed, requiredAgreed, selectedSlot])

  const loadOptions = useCallback(
    async (reason: 'initial' | 'level-change' | 'interval' | 'focus' | 'pageshow' | 'race') => {
      if (!bearerToken || (submittingRef.current && reason !== 'race')) {
        return
      }

      const requestSequence = ++requestSequenceRef.current
      if (!optionsRef.current) {
        setLoadingOptions(true)
      }
      if (reason === 'initial' || reason === 'level-change') {
        setErrorMessage(null)
      }

      try {
        const result = await requestOnboardingBookingOptions(
          {
            language,
            selectedLevel: level,
            preferredTime: [],
            timezoneCode: profile.timezoneCode || 'Asia/Tokyo',
            locale: effectiveLocale,
          },
          bearerToken,
        )
        if (requestSequence !== requestSequenceRef.current) {
          return
        }

        const fullRangeCompliantOptions = applyJpTrialRangePolicy(result)
        const visibleRangeSourceOptions = applyJpTrialRangePolicy(
          fullRangeCompliantOptions,
          visibleRangeDaysRef.current,
        )
        const policyCompliantOptions = applyJpTrialSlotPolicy(
          fullRangeCompliantOptions,
          language,
          visibleRangeDaysRef.current,
        )
        const previousOptions = optionsRef.current
        const currentSelectedSlot = selectedSlotRef.current
        const selectedSlotExpired = Boolean(
          currentSelectedSlot?.utcClassDateTime &&
          !isOpenSlot(policyCompliantOptions, currentSelectedSlot.utcClassDateTime),
        )
        const previousOpenSlots = new Set(previousOptions?.recommendedSlots.map((slot) => slot.utcClassDateTime) ?? [])
        const nextOpenSlots = new Set(policyCompliantOptions.recommendedSlots.map((slot) => slot.utcClassDateTime))
        const removedCount = [...previousOpenSlots].filter((slot) => !nextOpenSlots.has(slot)).length

        fullOptionsRef.current = fullRangeCompliantOptions
        optionsRef.current = policyCompliantOptions
        setOptions(policyCompliantOptions)
        if (selectedSlotExpired) {
          selectedSlotRef.current = null
          setSelectedSlot(null)
          if (reason !== 'level-change') {
            setAvailabilityNotice(message('lesson.jpTrial.schedule.expiredRecommendation'))
          }
        } else if (currentSelectedSlot) {
          const localizedRecommendedSlot = policyCompliantOptions.recommendedSlots.find(
            (slot) => slot.utcClassDateTime === currentSelectedSlot.utcClassDateTime,
          )
          const localizedDate = policyCompliantOptions.dates.find(
            (date) => date.fullDate === currentSelectedSlot.fullDate,
          )
          const localizedSelectedSlot =
            localizedRecommendedSlot ??
            (localizedDate
              ? {
                  ...currentSelectedSlot,
                  label: `${localizedDate.day} ${currentSelectedSlot.time}`,
                }
              : currentSelectedSlot)
          selectedSlotRef.current = localizedSelectedSlot
          setSelectedSlot(localizedSelectedSlot)
        }

        const empty = policyCompliantOptions.recommendedSlots.length === 0
        const emptyTrackingKey = `${language}:${level}:${visibleRangeDaysRef.current}`
        if (empty && !emptyTrackedKeysRef.current.has(emptyTrackingKey)) {
          emptyTrackedKeysRef.current.add(emptyTrackingKey)
          trackJpTrialEvent('trial_slot_empty_view', language, {
            market: 'JP',
            level,
            range_days: visibleRangeDaysRef.current,
            reason: getJpTrialSlotEmptyReason(policyCompliantOptions, visibleRangeSourceOptions),
          })
        } else if (!empty) {
          emptyTrackedKeysRef.current.delete(emptyTrackingKey)
        }
        trackJpTrialEvent('trial_slot_refresh', language, {
          market: 'JP',
          trigger:
            reason === 'pageshow' || reason === 'race'
              ? 'reentry'
              : reason === 'level-change'
                ? 'level_change'
                : reason,
          range_days: visibleRangeDaysRef.current,
          entry_hour_jst: Number(
            new Intl.DateTimeFormat('en-US', {
              hour: '2-digit',
              hour12: false,
              timeZone: 'Asia/Tokyo',
            }).format(new Date()),
          ),
          slot_count: policyCompliantOptions.recommendedSlots.length,
          removed_count: removedCount,
          selected_slot_expired: selectedSlotExpired,
        })
      } catch (error) {
        if (requestSequence !== requestSequenceRef.current) {
          return
        }
        if (error && typeof error === 'object' && 'status' in error && (error as { status?: unknown }).status === 401) {
          trackJpTrialEvent('trial_session_expired', language, {
            market: 'JP',
            stage: 'schedule',
            restored: true,
          })
          if (!sessionRedirectingRef.current) {
            sessionRedirectingRef.current = true
            router.replace('/login?destination=TRIAL_APPLICATION')
          }
          return
        }
        setErrorMessage(message('lesson.jpTrial.schedule.loadError'))
      } finally {
        if (requestSequence === requestSequenceRef.current) {
          setLoadingOptions(false)
        }
      }
    },
    [bearerToken, effectiveLocale, language, level, profile.timezoneCode, router],
  )

  useEffect(() => {
    const levelChanged = previousLevelRef.current !== level
    previousLevelRef.current = level
    if (levelChanged) {
      selectedSlotRef.current = null
      setSelectedSlot(null)
      setAvailabilityNotice(null)
      setIntentKey(createBookingIntentKey())
    }
    void loadOptions(levelChanged ? 'level-change' : 'initial')
  }, [level, loadOptions])

  const openExpandedSchedule = useCallback(() => {
    const currentRangeDays = visibleRangeDaysRef.current
    const nextRangeDays = getNextJpTrialRangeDays(currentRangeDays)
    const fullOptions = fullOptionsRef.current

    if (fullOptions && nextRangeDays > currentRangeDays) {
      const expandedSourceOptions = applyJpTrialRangePolicy(fullOptions, nextRangeDays)
      const expandedOptions = applyJpTrialSlotPolicy(fullOptions, language, nextRangeDays)
      visibleRangeDaysRef.current = nextRangeDays
      setVisibleRangeDays(nextRangeDays)
      optionsRef.current = expandedOptions
      setOptions(expandedOptions)
      trackJpTrialEvent('trial_range_expanded', language, {
        market: 'JP',
        level,
        from_days: currentRangeDays,
        to_days: nextRangeDays,
      })

      const empty = expandedOptions.recommendedSlots.length === 0
      const emptyTrackingKey = `${language}:${level}:${nextRangeDays}`
      if (empty && !emptyTrackedKeysRef.current.has(emptyTrackingKey)) {
        emptyTrackedKeysRef.current.add(emptyTrackingKey)
        trackJpTrialEvent('trial_slot_empty_view', language, {
          market: 'JP',
          level,
          range_days: nextRangeDays,
          reason: getJpTrialSlotEmptyReason(expandedOptions, expandedSourceOptions),
        })
      }
    }

    setCalendarOpen(true)
    trackJpTrialEvent('trial_bottomsheet_open', language, {
      market: 'JP',
      range_days: nextRangeDays,
    })
  }, [language, level])

  useEffect(() => {
    const refreshInterval = window.setInterval(() => void loadOptions('interval'), OPTIONS_REFRESH_INTERVAL_MS)
    const handleFocus = () => void loadOptions('focus')
    const handlePageShow = () => void loadOptions('pageshow')
    window.addEventListener('focus', handleFocus)
    window.addEventListener('pageshow', handlePageShow)

    return () => {
      window.clearInterval(refreshInterval)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [loadOptions])

  const handleEmailChange = (value: string) => {
    const nextValue = value.slice(0, 254)
    const activeEmail = activeVerificationEmailRef.current
    if (activeEmail && normalizeEmail(activeEmail) !== normalizeEmail(nextValue)) {
      activeVerificationEmailRef.current = null
      if (bearerToken) {
        void invalidateJpTrialEmailVerificationCode(activeEmail, bearerToken).catch(() => undefined)
      }
    }

    setEmail(nextValue)
    setEmailVerificationCode('')
    setEmailStatusMessage(null)
    if (normalizeEmail(nextValue) !== normalizeEmail(emailVerification?.email ?? '')) {
      setEmailVerification(null)
    }
  }

  const applyEmailVerificationResult = (result: JpTrialEmailVerification) => {
    setEmailVerification(result)
    setClockNow(Date.now())
    if (result.serverTime) {
      setServerClockOffsetMs(result.serverTime - Date.now())
    }
    setEmailRetryAvailableAt(result.retryAfterSeconds ? Date.now() + result.retryAfterSeconds * 1_000 : null)
    const statusMessageKey = EMAIL_STATUS_MESSAGE_KEY[result.status]
    setEmailStatusMessage(
      result.status === 'INVALID_CODE' && result.remainingAttempts !== null
        ? message('lesson.jpTrial.email.status.invalidCodeRemaining', { count: result.remainingAttempts })
        : statusMessageKey
          ? message(statusMessageKey)
          : null,
    )

    if (['CODE_SENT', 'COOLDOWN', 'INVALID_CODE'].includes(result.status)) {
      activeVerificationEmailRef.current = result.email ?? normalizedEmail
    }
    if (result.status === 'VERIFIED') {
      activeVerificationEmailRef.current = null
      setEmailVerificationCode('')
      void queryClient.invalidateQueries({ queryKey: userEntityQueries.all })
    }
    if (result.status === 'DUPLICATE_EMAIL') {
      activeVerificationEmailRef.current = null
      setDuplicateEmailDialogOpen(true)
      trackJpTrialEvent('trial_duplicate_email_block', language, {
        market: 'JP',
        source: 'email_verification',
      })
    }
  }

  const sendEmailVerificationCode = async () => {
    setEmailTouched(true)
    if (!bearerToken || !emailValid || emailVerificationBusy || resendSeconds > 0 || retrySeconds > 0) {
      return
    }

    setEmailVerificationBusy(true)
    setEmailStatusMessage(null)
    try {
      const result = await requestJpTrialEmailVerificationCode(normalizedEmail, bearerToken)
      applyEmailVerificationResult(result)
      trackJpTrialEvent('trial_email_verification_request', language, {
        market: 'JP',
        result: result.status,
      })
    } catch {
      setEmailStatusMessage(message('lesson.jpTrial.email.status.sendFailed'))
    } finally {
      setEmailVerificationBusy(false)
    }
  }

  const verifyEmailCode = async () => {
    if (
      !bearerToken ||
      !emailValid ||
      emailVerificationCode.length !== 6 ||
      emailVerificationBusy ||
      activeVerificationEmailRef.current !== normalizedEmail
    ) {
      return
    }

    setEmailVerificationBusy(true)
    setEmailStatusMessage(null)
    try {
      const result = await confirmJpTrialEmailVerificationCode(normalizedEmail, emailVerificationCode, bearerToken)
      applyEmailVerificationResult(result)
      trackJpTrialEvent('trial_email_verification_confirm', language, {
        market: 'JP',
        result: result.status,
      })
    } catch {
      setEmailStatusMessage(message('lesson.jpTrial.email.status.verifyFailed'))
    } finally {
      setEmailVerificationBusy(false)
    }
  }

  const hasCompletedProfile = hasCompletedJpTrialProfile({
    availableLanguages,
    language,
    nickname,
    emailValid,
    emailVerified,
  })
  const hasMarketingConsent = initialMarketingAgreed || marketingAgreed
  const canSubmit =
    hasCompletedProfile &&
    Boolean(selectedSlot?.utcClassDateTime) &&
    requiredAgreed &&
    hasMarketingConsent &&
    !submitting

  const selectedSlotLabel = useMemo(() => selectedSlot?.label ?? '', [selectedSlot])
  const submit = async () => {
    if (!canSubmit || submittingRef.current || !bearerToken || !selectedSlot?.utcClassDateTime) {
      return
    }

    submittingRef.current = true
    requestSequenceRef.current += 1
    setSubmitting(true)
    setCalendarOpen(false)
    setErrorMessage(null)
    setAvailabilityNotice(null)
    const bookingStartedAt = performance.now()
    trackJpTrialEvent('trial_cta_click', language, {
      market: 'JP',
      level,
      slot_time: selectedSlot.utcClassDateTime,
      marketing_agreed: marketingAgreed,
    })

    try {
      let bookingResult: Awaited<ReturnType<typeof requestOnboardingBookingConfirm>> | null = null
      let confirmationUncertain = false
      let reconciledFromState = false

      try {
        await updateProfile.mutateAsync({ name: nickname.trim() })
        await requestOnboardingMarketingConsent(
          {
            marketingAgreed,
            language,
            requiredTermsAgreed: requiredAgreed,
            termsDocumentVersion: JP_TRIAL_TERMS_VERSION,
          },
          bearerToken,
        )

        try {
          bookingResult = await requestOnboardingBookingConfirm(
            {
              language,
              selectedLevel: level,
              utcClassDateTime: selectedSlot.utcClassDateTime,
              idempotencyKey: intentKey,
            },
            bearerToken,
          )
        } catch (error) {
          const errorType = classifyJpTrialBookingError(error)
          const status =
            error && typeof error === 'object' && 'status' in error ? (error as { status?: unknown }).status : null
          confirmationUncertain =
            errorType === 'RETRYABLE' || (errorType === 'FATAL' && (typeof status !== 'number' || status >= 500))
          // 명확한 거절은 그대로 안내한다. 응답 수신·파싱이 불확실할 때만 실제 활성 예약을 확인한다.
          if (confirmationUncertain) {
            try {
              const state = await requestOnboardingState(bearerToken, language)
              const requestedTime = parseUtcBookingTime(selectedSlot.utcClassDateTime)
              const expectedLanguage = language === 'ko' ? 'KR' : 'EN'
              const levelIndex = TRIAL_LEVELS.findIndex((item) => item.value === level)
              const expectedLevel = (language === 'ko' ? KO_ONBOARDING_LEVEL_LABELS : EN_ONBOARDING_LEVEL_LABELS)[
                levelIndex
              ]
              let activeLecture: Awaited<ReturnType<typeof getReservedLessons>>['lectures'][number] | undefined
              if (state.trialBooked && state.selectedClassId !== null && state.language === expectedLanguage) {
                const pageSize = 50
                let page = 1
                let total = 1
                while ((page - 1) * pageSize < total) {
                  const reservedLessons = await getReservedLessons(bearerToken, effectiveLocale, {
                    page,
                    limit: pageSize,
                  })
                  activeLecture = reservedLessons.lectures.find((lecture) => lecture.class_id === state.selectedClassId)
                  if (activeLecture || reservedLessons.lectures.length === 0) {
                    break
                  }
                  total = reservedLessons.total
                  page += 1
                }
              }
              if (
                state.language === expectedLanguage &&
                state.trialBooked &&
                state.selectedClassId !== null &&
                activeLecture?.invoice_status === 'RESERVED' &&
                activeLecture.lang_type === expectedLanguage &&
                getTrialLevelLabelFromClassroomParams(language, activeLecture.level, activeLecture.week) ===
                  expectedLevel &&
                Number.isFinite(requestedTime) &&
                parseUtcBookingTime(activeLecture.utc_class_datetime) === requestedTime &&
                parseUtcBookingTime(state.selectedClassDatetime) === requestedTime
              ) {
                bookingResult = {
                  selectedClassId: activeLecture.class_id,
                  prestudyClassId: state.prestudyClassId,
                  selectedClassDatetime: selectedSlot.utcClassDateTime,
                  trialBooked: true,
                }
                reconciledFromState = true
              }
            } catch {
              // 원래 예약 오류를 유지하고 결과 미확정으로 안내한다.
            }
          }
          if (!bookingResult) {
            throw error
          }
        }
      } catch (error) {
        const errorType = classifyJpTrialBookingError(error)
        const result = confirmationUncertain
          ? 'unconfirmed'
          : errorType === 'ALREADY_BOOKED'
            ? 'duplicate'
            : errorType === 'SLOT_UNAVAILABLE' || errorType === 'TOO_SOON'
              ? 'race_fail'
              : 'network_fail'
        trackJpTrialEvent('trial_booking_result', language, {
          market: 'JP',
          result,
          error_type: errorType,
          latency_ms: Math.round(performance.now() - bookingStartedAt),
        })
        if (confirmationUncertain) {
          setErrorMessage(message('lesson.jpTrial.errors.resultUnknown'))
        } else if (errorType === 'SLOT_UNAVAILABLE' || errorType === 'TOO_SOON') {
          setErrorMessage(null)
          selectedSlotRef.current = null
          setSelectedSlot(null)
          setRaceDialogOpen(true)
          trackJpTrialEvent('trial_race_dialog_view', language, { market: 'JP', error_type: errorType })
          void loadOptions('race')
        } else {
          setErrorMessage(message(ERROR_MESSAGE_KEY[errorType]))
        }
        if (errorType === 'ALREADY_BOOKED') {
          trackJpTrialEvent('trial_duplicate_block', language, {
            market: 'JP',
            stage: 'cta',
            reason: 'existing_booking',
          })
        }
        return
      }

      // 이 지점부터는 API 응답 또는 같은 언어·시간의 서버 상태로 예약 성공을 확인했다.
      dismissJpTrialApplicationForSession(userId)
      try {
        window.sessionStorage.removeItem(JP_TRIAL_DRAFT_STORAGE_KEY)
      } catch {
        // 저장소 정리 실패는 이미 완료된 예약에 영향을 주지 않는다.
      }
      trackJpTrialEvent('trial_booking_result', language, {
        market: 'JP',
        result: 'success',
        confirmation_source: reconciledFromState ? 'server_state' : 'response',
        latency_ms: Math.round(performance.now() - bookingStartedAt),
      })
      setCompletedSlotLabel(selectedSlot.label)
      try {
        const desktop = !isApp && window.matchMedia('(min-width: 768px)').matches
        trackJpTrialEvent('trial_complete_view', language, {
          market: 'JP',
          booking_id: bookingResult.selectedClassId,
          surface: isApp ? 'app_popup' : desktop ? 'desktop_qr' : 'mobile_popup',
        })
        if (desktop) {
          const completionParams = new URLSearchParams({
            jpTrialCompleted: 'true',
            bookedAt: selectedSlot.label,
          })
          router.replace(`/home?${completionParams.toString()}`)
        } else {
          setCompleted(true)
        }
      } catch {
        setCompleted(true)
      }
      try {
        void queryClient.invalidateQueries({ queryKey: userEntityQueries.all }).catch(() => {
          // 캐시 재조회 실패와 예약 확정 여부는 별개다.
        })
      } catch {
        // 캐시 갱신 시작 실패도 완료 화면에 영향을 주지 않는다.
      }
    } finally {
      submittingRef.current = false
      setSubmitting(false)
    }
  }

  const selectLanguage = (nextLanguage: LessonLanguage) => {
    if (submittingRef.current || nextLanguage === language || !availableLanguages.includes(nextLanguage)) {
      return
    }

    setLanguage(nextLanguage)
    setLevel('BEGINNER')
    setOptions(null)
    optionsRef.current = null
    fullOptionsRef.current = null
    selectedSlotRef.current = null
    setSelectedSlot(null)
    setAvailabilityNotice(null)
    setErrorMessage(null)
    setCalendarOpen(false)
    setIntentKey(createBookingIntentKey())
    emptyTrackedKeysRef.current.clear()
    trackJpTrialEvent('trial_language_select', nextLanguage, {
      market: 'JP',
    })
  }

  return (
    <main
      className="mx-auto min-h-dvh w-full max-w-[768px] bg-white text-[#1C1C1C]"
      data-testid="page-jp-trial-application"
    >
      <header className="sticky top-0 z-20 flex h-[52px] items-center justify-center border-b bg-white">
        <h1 className="text-[18px] font-bold">{t('lesson.jpTrial.title')}</h1>
        <button
          type="button"
          disabled={submitting}
          onClick={() => {
            dismissJpTrialApplicationForSession(userId)
            router.replace('/home')
          }}
          aria-label={t('lesson.jpTrial.close')}
          className="absolute right-5"
        >
          <X size={24} />
        </button>
      </header>

      <HomeBannerCarousel countryCode="JP" placement="JP_TRIAL" />

      <fieldset disabled={submitting || completed} className="min-w-0 space-y-8 px-5 pb-10 pt-7">
        <section className="space-y-3">
          <h2 className="font-bold">{t('lesson.jpTrial.language.title')}</h2>
          <SelectionCard
            compact
            selected={language === 'ko'}
            disabled={!availableLanguages.includes('ko')}
            onClick={() => selectLanguage('ko')}
          >
            <span className="text-[20px] leading-none">🇰🇷</span>
            <span className="font-bold">{t('lesson.jpTrial.language.korean')}</span>
          </SelectionCard>
          <SelectionCard
            compact
            selected={language === 'en'}
            disabled={!availableLanguages.includes('en')}
            onClick={() => selectLanguage('en')}
          >
            <span className="text-[20px] leading-none">🇺🇸</span>
            <span className="font-bold">{t('lesson.jpTrial.language.english')}</span>
          </SelectionCard>
        </section>

        <section className="space-y-5">
          <label className="block">
            <span className="font-bold">{t('lesson.jpTrial.profile.nickname')}</span>
            <span className="mt-1 block text-xs text-[#9297A3]">{t('lesson.jpTrial.profile.nicknameGuide')}</span>
            <input
              value={nickname}
              onChange={(event) => setNickname(sanitizeEnglishNickname(event.target.value))}
              onBlur={() => setNicknameTouched(true)}
              inputMode="text"
              autoComplete="name"
              aria-invalid={nicknameTouched && !isValidEnglishNickname(nickname)}
              className="mt-3 h-[52px] w-full rounded-[12px] border border-[#D6D6D6] px-4 outline-none focus:border-[#6ABE36]"
            />
            <span className="mt-1 flex justify-between text-xs text-[#A0A0A0]">
              <span>{t('lesson.jpTrial.profile.nicknameHelp')}</span>
              <span>{nickname.length} / 20</span>
            </span>
            {nicknameTouched && !isValidEnglishNickname(nickname) ? (
              <span className="mt-1 block text-xs text-[#C43C3C]">
                {t(
                  nickname.trim()
                    ? 'lesson.jpTrial.profile.nicknameEnglishOnly'
                    : 'lesson.jpTrial.profile.nicknameRequired',
                )}
              </span>
            ) : null}
          </label>
          <div className="block">
            <label htmlFor="jp-trial-email" className="font-bold">
              {t('lesson.jpTrial.email.label')}
            </label>
            <span className="mt-1 block text-xs text-[#9297A3]">{t('lesson.jpTrial.email.guide')}</span>
            <div className="relative mt-3 flex gap-2">
              <input
                id="jp-trial-email"
                value={email}
                onChange={(event) => handleEmailChange(event.target.value)}
                onBlur={() => setEmailTouched(true)}
                inputMode="email"
                autoComplete="email"
                aria-invalid={emailTouched && !emailValid}
                className={clsx(
                  'h-[52px] min-w-0 flex-1 rounded-[12px] border px-4 outline-none focus:border-[#6ABE36]',
                  emailVerified && 'pr-[112px]',
                  emailTouched && !emailValid ? 'border-[#E45656]' : 'border-[#D6DADC]',
                )}
              />
              {emailVerified ? (
                <span className="pointer-events-none absolute right-3 mt-[16px] rounded-full border border-[#70DF9B] bg-[#E8FFF0] px-2 py-1 text-[10px] font-bold text-[#06C755]">
                  ● {t('lesson.jpTrial.email.verified')}
                </span>
              ) : (
                <button
                  type="button"
                  disabled={!emailValid || emailVerificationBusy || resendSeconds > 0 || retrySeconds > 0}
                  onClick={() => void sendEmailVerificationCode()}
                  className="h-[52px] min-w-[92px] rounded-[12px] border border-[#6ABE36] px-3 text-sm font-bold text-[#457B23] disabled:border-[#DDE1E8] disabled:text-[#A6AAB3]"
                >
                  {resendSeconds > 0
                    ? t('lesson.jpTrial.email.seconds', { seconds: resendSeconds })
                    : activeVerificationEmailRef.current
                      ? t('lesson.jpTrial.email.resend')
                      : t('lesson.jpTrial.email.verify')}
                </button>
              )}
            </div>
            {emailTouched && !emailValid ? (
              <span className="mt-1 block text-xs text-[#C43C3C]">{t('lesson.jpTrial.email.invalid')}</span>
            ) : null}
            {showVerificationCode ? (
              <div className="mt-3">
                <div className="flex gap-2">
                  <input
                    value={emailVerificationCode}
                    onChange={(event) => setEmailVerificationCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder={t('lesson.jpTrial.email.codePlaceholder')}
                    aria-label={t('lesson.jpTrial.email.codePlaceholder')}
                    className="h-[52px] min-w-0 flex-1 rounded-[12px] border border-[#D6DADC] px-4 tracking-[0.25em] outline-none focus:border-[#6ABE36]"
                  />
                  <button
                    type="button"
                    disabled={emailVerificationCode.length !== 6 || emailVerificationBusy}
                    onClick={() => void verifyEmailCode()}
                    className="h-[52px] min-w-[92px] rounded-[12px] bg-[#6ABE36] px-3 text-sm font-bold text-white disabled:bg-[#E1E3E8]"
                  >
                    {t('lesson.jpTrial.email.confirm')}
                  </button>
                </div>
                <p className="mt-2 text-xs leading-5 text-[#9297A3]">{t('lesson.jpTrial.email.deliveryHelp')}</p>
              </div>
            ) : null}
            {emailStatusMessage ? (
              <span
                aria-live="polite"
                className={clsx('mt-2 block text-xs leading-5', emailVerified ? 'text-[#378C2B]' : 'text-[#676C76]')}
              >
                {t(emailStatusMessage.key, emailStatusMessage.values)}
                {retrySeconds > 0
                  ? t('lesson.jpTrial.email.retryAfterMinutes', { minutes: Math.ceil(retrySeconds / 60) })
                  : ''}
              </span>
            ) : null}
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="font-bold">
            {t('lesson.jpTrial.level.title', {
              language: t(language === 'ko' ? 'lesson.jpTrial.language.korean' : 'lesson.jpTrial.language.english'),
            })}
          </h2>
          {TRIAL_LEVELS.map((item) => (
            <SelectionCard
              key={item.value}
              selected={level === item.value}
              onClick={() => {
                setLevel(item.value)
                trackJpTrialEvent('trial_level_select', language, {
                  market: 'JP',
                  level: item.value,
                  is_default: item.value === 'BEGINNER',
                  has_slot_selected: Boolean(selectedSlot),
                })
              }}
            >
              <LevelIcon bars={item.bars} />
              <span className="min-w-0">
                <span className="block font-bold">
                  {t(`lesson.jpTrial.level.names.${item.value}`)}{' '}
                  {item.featured ? <span className="text-[#6ABE36]">★</span> : null}
                </span>
                {item.featured ? (
                  <span
                    className={clsx(
                      'mt-0.5 block text-xs font-medium leading-[18px]',
                      level === item.value ? 'text-[#457B23]' : 'text-[#777]',
                    )}
                  >
                    {t('lesson.jpTrial.level.beginnerBadge')}
                  </span>
                ) : null}
                <span
                  className={clsx(
                    'mt-0.5 block text-xs leading-[18px]',
                    level === item.value ? 'text-[#457B23]' : 'text-[#777]',
                  )}
                >
                  {t(`lesson.jpTrial.level.descriptions.${language}.${item.value}`)}
                </span>
              </span>
            </SelectionCard>
          ))}
        </section>

        <section className="space-y-3">
          {options ? experimentTracker : null}
          <h2 className="font-bold">{t('lesson.jpTrial.schedule.title')}</h2>
          {loadingOptions ? <div className="h-[164px] animate-pulse rounded-xl bg-[#F3F4F6]" /> : null}
          {!loadingOptions && options ? (
            options.recommendedSlots.length > 0 ? (
              <>
                <div className="grid grid-cols-2 gap-2">
                  {displayedRecommendedSlots.map((slot) => (
                    <button
                      key={slot.id}
                      type="button"
                      aria-pressed={selectedSlot?.utcClassDateTime === slot.utcClassDateTime}
                      onClick={() => {
                        selectedSlotRef.current = slot
                        setSelectedSlot(slot)
                        setAvailabilityNotice(null)
                        setIntentKey(createBookingIntentKey())
                        trackJpTrialEvent('trial_slot_select', language, {
                          market: 'JP',
                          source: 'reco',
                          slot_time: slot.utcClassDateTime,
                        })
                      }}
                      className={clsx(
                        'min-h-[52px] rounded-[12px] border px-2 text-base font-medium outline-none focus-visible:ring-2 focus-visible:ring-[#6ABE36] focus-visible:ring-offset-1',
                        selectedSlot?.utcClassDateTime === slot.utcClassDateTime
                          ? 'border-[1.5px] border-[#6ABE36] bg-[#F2FCEC]'
                          : 'border-[#E8E8E8]',
                      )}
                    >
                      {slot.label}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={openExpandedSchedule}
                  className="flex h-[52px] w-full items-center justify-center rounded-[12px] border border-[#E8E8E8] font-bold shadow-[0_5px_0_#E8E8E8] outline-none focus-visible:ring-2 focus-visible:ring-[#6ABE36] focus-visible:ring-offset-1"
                >
                  {t('lesson.jpTrial.schedule.more')}
                </button>
              </>
            ) : (
              <div className="rounded-[10px] bg-[#F7F8FA] px-5 py-6 text-center">
                <p className="font-bold">{t('lesson.jpTrial.schedule.emptyTitle')}</p>
                <p className="mt-2 text-sm leading-6 text-[#757A86]">
                  {t(
                    visibleRangeDays < 7
                      ? 'lesson.jpTrial.schedule.emptyExpandableDescription'
                      : 'lesson.jpTrial.schedule.emptyDescription',
                  )}
                </p>
                {visibleRangeDays < 7 ? (
                  <button
                    type="button"
                    onClick={openExpandedSchedule}
                    className="mt-4 text-sm font-bold text-[#5379FF]"
                  >
                    {t('lesson.jpTrial.schedule.more')} ›
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      trackJpTrialEvent('trial_cs_contact_click', language, {
                        market: 'JP',
                        source: 'slot_empty',
                        level,
                      })
                      const openOnetalk = window.Onetalk?.open?.bind(window.Onetalk)
                      if (openOnetalk) {
                        void Promise.resolve(openOnetalk()).catch(() => router.push(CUSTOMER_CENTER_URL))
                      } else {
                        router.push(CUSTOMER_CENTER_URL)
                      }
                    }}
                    className="mt-4 text-sm font-bold text-[#5379FF]"
                  >
                    {t('lesson.jpTrial.schedule.contact')} ›
                  </button>
                )}
              </div>
            )
          ) : null}
          {availabilityNotice ? (
            <p aria-live="polite" className="text-sm leading-5 text-[#C43C3C]">
              {t(availabilityNotice.key, availabilityNotice.values)}
            </p>
          ) : null}
          {selectedSlot ? (
            <p aria-live="polite" className="text-sm font-semibold text-[#457B23]">
              {t('lesson.jpTrial.schedule.selected', { slot: selectedSlotLabel })}
            </p>
          ) : null}
        </section>

        <section className="space-y-4">
          <div>
            <h2 className="font-bold">{t('lesson.jpTrial.ticket.sectionTitle')}</h2>
          </div>
          <SelectionCard selected>
            <Image
              src="/images/trial/jp-trial-ticket.png"
              alt=""
              width={46}
              height={46}
              className="h-[46px] w-[46px] shrink-0"
            />
            <span className="min-w-0">
              <span className="block font-bold">
                {t('lesson.jpTrial.ticket.name', { minutes: TRIAL_DURATION_MINUTES[language] })}
              </span>
              {language === 'ko' ? (
                <span className="mt-0.5 block text-[11px] leading-[18px] text-[#777]">
                  {t('lesson.jpTrial.ticket.consultation')}
                </span>
              ) : null}
              <span className="mt-0.5 block text-[10px] leading-[17px] text-[#6ABE36]">
                {t('lesson.jpTrial.ticket.perLanguage')}
                <br />
                {t('lesson.jpTrial.ticket.changeGuide')}
              </span>
            </span>
          </SelectionCard>
          <button
            type="button"
            onClick={() => {
              const allAgreed = requiredAgreed && hasMarketingConsent
              const nextAgreed = !allAgreed
              setRequiredAgreed(nextAgreed)
              if (!initialMarketingAgreed) {
                setMarketingAgreed(nextAgreed)
              }
              trackJpTrialEvent('trial_agree_toggle', language, {
                market: 'JP',
                type: 'all',
                checked: nextAgreed,
              })
            }}
            className="flex min-h-[52px] w-full items-center gap-3 rounded-[12px] border border-[#E8E8E8] px-4 py-3 text-left text-base font-bold"
          >
            <CheckCircle selected={requiredAgreed && hasMarketingConsent} /> {t('lesson.jpTrial.terms.agreeAll')}
          </button>
          <div className="flex w-full items-center px-4 text-left text-sm text-[#676C76]">
            <button
              type="button"
              aria-label={t('lesson.jpTrial.terms.toggleRequired')}
              aria-pressed={requiredAgreed}
              onClick={() =>
                setRequiredAgreed((value) => {
                  trackJpTrialEvent('trial_agree_toggle', language, {
                    market: 'JP',
                    type: 'required',
                    checked: !value,
                  })
                  return !value
                })
              }
              className="shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-[#6ABE36] focus-visible:ring-offset-1"
            >
              <CheckCircle selected={requiredAgreed} />
            </button>
            <span className="ml-3 shrink-0">{t('lesson.jpTrial.terms.prefix')}</span>
            <BottomSheetRoot>
              <BottomSheetTrigger asChild>
                <button
                  type="button"
                  className="shrink-0 font-semibold text-[#5379FF] underline outline-none focus-visible:ring-2 focus-visible:ring-[#6ABE36] focus-visible:ring-offset-1"
                >
                  {t('lesson.jpTrial.terms.link')}
                </button>
              </BottomSheetTrigger>
              <PaymentTermsInformationBottomSheet />
            </BottomSheetRoot>
            <span className="shrink-0">{t('lesson.jpTrial.terms.suffix')}</span>
          </div>
          {!initialMarketingAgreed ? (
            <button
              type="button"
              onClick={() =>
                setMarketingAgreed((value) => {
                  trackJpTrialEvent('trial_agree_toggle', language, {
                    market: 'JP',
                    type: 'marketing',
                    checked: !value,
                  })
                  return !value
                })
              }
              className="flex w-full items-center gap-3 px-4 text-left text-sm text-[#676C76]"
            >
              <CheckCircle selected={marketingAgreed} /> {t('lesson.jpTrial.terms.marketing')}
            </button>
          ) : null}
        </section>

        {errorMessage ? (
          <div role="alert" className="rounded-[10px] bg-[#FFF2F2] px-4 py-3 text-sm leading-5 text-[#C43C3C]">
            {t(errorMessage.key, errorMessage.values)}
          </div>
        ) : null}

        <button
          type="button"
          disabled={!canSubmit}
          onClick={() => void submit()}
          className="h-[52px] w-full rounded-[10px] border border-[#111] bg-[#B5FD4C] font-bold text-[#111] shadow-[0_5px_0_#111] outline-none focus-visible:ring-2 focus-visible:ring-[#6ABE36] focus-visible:ring-offset-1 disabled:border-[#E8E8E8] disabled:bg-[#F5F5F5] disabled:text-[#D6D6D6] disabled:shadow-[0_5px_0_#E8E8E8]"
        >
          {t(submitting ? 'lesson.jpTrial.submit.submitting' : 'lesson.jpTrial.submit.action')}
        </button>
      </fieldset>

      {calendarOpen && options ? (
        <CalendarSheet
          {...options}
          selectedSlot={selectedSlot}
          onClose={() => setCalendarOpen(false)}
          onCommit={(slot) => {
            if (submittingRef.current) {
              return
            }
            selectedSlotRef.current = slot
            setSelectedSlot(slot)
            setAvailabilityNotice(null)
            setIntentKey(createBookingIntentKey())
            setCalendarOpen(false)
            trackJpTrialEvent('trial_bottomsheet_confirm', language, {
              market: 'JP',
              date_tab: slot.fullDate,
              selected_time: slot.utcClassDateTime,
            })
            trackJpTrialEvent('trial_slot_select', language, {
              market: 'JP',
              source: 'bottomsheet',
              slot_time: slot.utcClassDateTime,
            })
          }}
        />
      ) : null}
      {raceDialogOpen ? (
        <RaceDialog
          onConfirm={() => {
            setRaceDialogOpen(false)
            setErrorMessage(null)
            setCalendarOpen(true)
            trackJpTrialEvent('trial_race_dialog_action', language, {
              market: 'JP',
              action: 'retry',
            })
          }}
        />
      ) : null}
      {duplicateEmailDialogOpen ? (
        <DuplicateEmailDialog
          onClose={() => setDuplicateEmailDialogOpen(false)}
          onContact={() => {
            trackJpTrialEvent('trial_cs_contact_click', language, {
              market: 'JP',
              source: 'duplicate_email',
            })
            const openOnetalk = window.Onetalk?.open?.bind(window.Onetalk)
            if (openOnetalk) {
              void Promise.resolve(openOnetalk()).catch(() => router.push(CUSTOMER_CENTER_URL))
            } else {
              router.push(CUSTOMER_CENTER_URL)
            }
          }}
        />
      ) : null}
      {completed ? (
        <CompletionDialog
          bookedSlotLabel={completedSlotLabel}
          durationMinutes={TRIAL_DURATION_MINUTES[language]}
          showLearningConsultation={language === 'ko'}
          isApp={isApp}
          onHome={() => {
            setCompleted(false)
            trackJpTrialEvent('trial_post_booking_route', language, {
              market: 'JP',
              route: 'home',
            })
            router.replace('/home')
          }}
          onAppOpen={() => {
            setCompleted(false)
            trackJpTrialEvent('trial_post_booking_route', language, {
              market: 'JP',
              route: 'deeplink_app',
            })
            window.location.assign('/open-in-app/home')
          }}
          onAppDownload={() => {
            setCompleted(false)
            trackJpTrialEvent('trial_post_booking_route', language, {
              market: 'JP',
              route: 'app_store',
            })
          }}
        />
      ) : null}
    </main>
  )
}
